import { useCallback, useEffect, useMemo, useRef } from 'react';

/** An invalidation is a read intent, never a replacement for the Controller snapshot. */
interface ProjectionInvalidation { session_id?: string }
export interface ProjectionRead { status: string; targetSessionId?: string }
interface Options {
  scopeKey: string;
  activeSessionId: string;
  read: ProjectionRead;
  readSession(sessionId: string): Promise<unknown>;
}
interface InvalidationScope {
  scopeKey: string;
  sessionId: string;
  readSession: Options['readSession'];
  active: boolean;
  revision: number;
  requested: number;
  read: ProjectionRead;
  pending?: Promise<unknown>;
}

/** Coalesce read intents around the existing read owner; no store, transport or cancellation engine. */
export const useProjectionInvalidation = ({ scopeKey, activeSessionId, read, readSession }: Options) => {
  const owner = useMemo<InvalidationScope>(() => ({ scopeKey, sessionId: activeSessionId, readSession,
    active: true, revision: 0, requested: 0,
    read: { status: 'idle' } }), [scopeKey, activeSessionId, readSession]);
  const current = useRef(owner);
  current.current = owner;
  owner.read = read;

  const flush = useCallback((): Promise<unknown> | undefined => {
    if (!owner.active || current.current !== owner || owner.pending || owner.read.status === 'pending'
        || owner.revision <= owner.requested) return owner.pending;
    owner.pending = Promise.resolve().then(() => {
      if (!owner.active || current.current !== owner || owner.read.status === 'pending') return;
      owner.requested = owner.revision;
      return owner.readSession(owner.sessionId);
    }).finally(() => {
      owner.pending = undefined;
      // A newer invalidation needs another read; failure alone never creates a retry.
      void flush()?.catch(() => {});
    });
    return owner.pending;
  }, [owner]);

  useEffect(() => {
    owner.active = true;
    return () => { owner.active = false; };
  }, [owner]);
  useEffect(() => { void flush()?.catch(() => {}); }, [flush, read.status, read.targetSessionId]);

  return useCallback((event?: ProjectionInvalidation) => {
    if (!owner.active || current.current !== owner || !activeSessionId
        || event && event.session_id !== activeSessionId) return;
    // No event means explicit reconnect recovery, not an invented backend event.
    owner.revision += 1;
    return flush();
  }, [activeSessionId, flush, owner]);
};
